
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

export default function Reports() {
  const navigate = useNavigate();
  const [selectedPeriod, setSelectedPeriod] = useState('month');
  const [selectedChart, setSelectedChart] = useState('overview');

  const monthlyData = {
    income: 8500,
    expenses: 6200,
    savings: 2300,
    categories: [
      { name: 'طعام', amount: 1800, percentage: 29, color: 'bg-green-500' },
      { name: 'مواصلات', amount: 1200, percentage: 19, color: 'bg-blue-500' },
      { name: 'فواتير', amount: 1500, percentage: 24, color: 'bg-yellow-500' },
      { name: 'ترفيه', amount: 800, percentage: 13, color: 'bg-purple-500' },
      { name: 'أخرى', amount: 900, percentage: 15, color: 'bg-gray-500' }
    ]
  };

  const weeklyExpenses = [
    { week: 'الأسبوع 1', amount: 1400 },
    { week: 'الأسبوع 2', amount: 1600 },
    { week: 'الأسبوع 3', amount: 1800 },
    { week: 'الأسبوع 4', amount: 1400 }
  ];

  const maxWeeklyAmount = Math.max(...weeklyExpenses.map(w => w.amount));

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="fixed top-0 w-full bg-white shadow-sm z-50">
        <div className="flex items-center justify-between px-6 py-4">
          <button 
            onClick={() => navigate('/dashboard')}
            className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-100"
          >
            <i className="ri-arrow-right-line text-xl text-gray-700"></i>
          </button>
          <h1 className="text-xl font-bold text-gray-800">التقارير المالية</h1>
          <div className="w-10"></div>
        </div>
      </div>

      {/* Content */}
      <div className="pt-20 pb-24 px-6">
        {/* Period Selector */}
        <div className="bg-white rounded-2xl p-4 mb-6">
          <div className="flex bg-gray-100 rounded-full p-1">
            <button 
              onClick={() => setSelectedPeriod('week')}
              className={`flex-1 py-2 px-4 rounded-full text-sm font-medium transition-all ${
                selectedPeriod === 'week' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-600'
              }`}
            >
              أسبوعي
            </button>
            <button 
              onClick={() => setSelectedPeriod('month')}
              className={`flex-1 py-2 px-4 rounded-full text-sm font-medium transition-all ${
                selectedPeriod === 'month' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-600'
              }`}
            >
              شهري
            </button>
            <button 
              onClick={() => setSelectedPeriod('year')}
              className={`flex-1 py-2 px-4 rounded-full text-sm font-medium transition-all ${
                selectedPeriod === 'year' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-600'
              }`}
            >
              سنوي
            </button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-gradient-to-br from-green-500 to-emerald-500 rounded-2xl p-4 text-white">
            <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center mb-2">
              <i className="ri-arrow-up-line text-lg"></i>
            </div>
            <p className="text-green-100 text-xs mb-1">الدخل</p>
            <p className="text-lg font-bold">{monthlyData.income.toLocaleString()}</p>
          </div>
          
          <div className="bg-gradient-to-br from-red-500 to-pink-500 rounded-2xl p-4 text-white">
            <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center mb-2">
              <i className="ri-arrow-down-line text-lg"></i>
            </div>
            <p className="text-red-100 text-xs mb-1">المصروفات</p>
            <p className="text-lg font-bold">{monthlyData.expenses.toLocaleString()}</p>
          </div>
          
          <div className="bg-gradient-to-br from-blue-500 to-indigo-500 rounded-2xl p-4 text-white">
            <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center mb-2">
              <i className="ri-wallet-line text-lg"></i>
            </div>
            <p className="text-blue-100 text-xs mb-1">الادخار</p>
            <p className="text-lg font-bold">{monthlyData.savings.toLocaleString()}</p>
          </div>
        </div>

        {/* Chart Type Selector */}
        <div className="bg-white rounded-2xl p-4 mb-6">
          <div className="flex space-x-2 space-x-reverse">
            <button
              onClick={() => setSelectedChart('overview')}
              className={`flex-1 py-2 px-4 rounded-xl text-sm font-medium transition-all ${
                selectedChart === 'overview' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600'
              }`}
            >
              نظرة عامة
            </button>
            <button
              onClick={() => setSelectedChart('categories')}
              className={`flex-1 py-2 px-4 rounded-xl text-sm font-medium transition-all ${
                selectedChart === 'categories' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600'
              }`}
            >
              الفئات
            </button>
          </div>
        </div>

        {selectedChart === 'overview' ? (
          <>
            {/* Weekly Expenses Chart */}
            <div className="bg-white rounded-2xl p-6 mb-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">المصروفات الأسبوعية</h3>
              <div className="space-y-4">
                {weeklyExpenses.map((week, index) => (
                  <div key={index} className="flex items-center space-x-3 space-x-reverse">
                    <div className="w-20 text-sm text-gray-600">{week.week}</div>
                    <div className="flex-1 bg-gray-100 rounded-full h-3 overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-blue-500 to-purple-500 rounded-full transition-all duration-1000"
                        style={{ width: `${(week.amount / maxWeeklyAmount) * 100}%` }}
                      ></div>
                    </div>
                    <div className="w-16 text-sm font-medium text-gray-800 text-left">
                      {week.amount.toLocaleString()}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Balance Trend */}
            <div className="bg-white rounded-2xl p-6 mb-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">اتجاه الرصيد</h3>
              <div className="relative h-32 bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-4">
                <img 
                  src="https://public.readdy.ai/ai/img_res/cd3c84b74158e637a44a168302d558cf.jpg"
                  alt="Balance Trend Chart"
                  className="w-full h-full object-cover rounded-lg"
                />
              </div>
            </div>
          </>
        ) : (
          <>
            {/* Categories Breakdown */}
            <div className="bg-white rounded-2xl p-6 mb-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">توزيع المصروفات حسب الفئة</h3>
              
              {/* Pie Chart Visualization */}
              <div className="relative w-48 h-48 mx-auto mb-6">
                <img 
                  src="https://readdy.ai/api/search-image?query=Colorful%20pie%20chart%20showing%20expense%20categories%20breakdown%2C%20modern%20flat%20design%2C%20vibrant%20colors%20including%20green%20blue%20yellow%20purple%20gray%2C%20clean%20professional%20financial%20visualization%2C%20white%20background&width=200&height=200&seq=expense-pie-chart&orientation=squarish"
                  alt="Expense Categories Pie Chart"
                  className="w-full h-full object-cover rounded-full"
                />
              </div>

              <div className="space-y-3">
                {monthlyData.categories.map((category, index) => (
                  <div key={index} className="flex items-center justify-between">
                    <div className="flex items-center space-x-3 space-x-reverse">
                      <div className={`w-4 h-4 rounded-full ${category.color}`}></div>
                      <span className="text-gray-700 font-medium">{category.name}</span>
                    </div>
                    <div className="text-left">
                      <p className="font-semibold text-gray-800">{category.amount.toLocaleString()} ريال</p>
                      <p className="text-sm text-gray-500">%{category.percentage}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Top Expenses */}
            <div className="bg-white rounded-2xl p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">أكبر المصروفات</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                  <div className="flex items-center space-x-3 space-x-reverse">
                    <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                      <i className="ri-restaurant-line text-green-600"></i>
                    </div>
                    <div>
                      <p className="font-medium text-gray-800">مطعم الأصالة</p>
                      <p className="text-sm text-gray-500">15 يناير 2024</p>
                    </div>
                  </div>
                  <p className="font-bold text-red-600">-450 ريال</p>
                </div>
                
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                  <div className="flex items-center space-x-3 space-x-reverse">
                    <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center">
                      <i className="ri-flashlight-line text-yellow-600"></i>
                    </div>
                    <div>
                      <p className="font-medium text-gray-800">فاتورة الكهرباء</p>
                      <p className="text-sm text-gray-500">12 يناير 2024</p>
                    </div>
                  </div>
                  <p className="font-bold text-red-600">-380 ريال</p>
                </div>
                
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                  <div className="flex items-center space-x-3 space-x-reverse">
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                      <i className="ri-gas-station-line text-blue-600"></i>
                    </div>
                    <div>
                      <p className="font-medium text-gray-800">محطة الوقود</p>
                      <p className="text-sm text-gray-500">10 يناير 2024</p>
                    </div>
                  </div>
                  <p className="font-bold text-red-600">-320 ريال</p>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Bottom Navigation */}
      <div className="fixed bottom-0 w-full bg-white border-t border-gray-200">
        <div className="grid grid-cols-4 py-2">
          <button 
            onClick={() => navigate('/dashboard')}
            className="flex flex-col items-center justify-center py-2"
          >
            <div className="w-6 h-6 flex items-center justify-center mb-1">
              <i className="ri-home-line text-gray-400 text-lg"></i>
            </div>
            <span className="text-xs text-gray-400">الرئيسية</span>
          </button>
          <button 
            onClick={() => navigate('/expenses')}
            className="flex flex-col items-center justify-center py-2"
          >
            <div className="w-6 h-6 flex items-center justify-center mb-1">
              <i className="ri-arrow-down-line text-gray-400 text-lg"></i>
            </div>
            <span className="text-xs text-gray-400">المصروفات</span>
          </button>
          <button 
            onClick={() => navigate('/reports')}
            className="flex flex-col items-center justify-center py-2"
          >
            <div className="w-6 h-6 flex items-center justify-center mb-1">
              <i className="ri-bar-chart-line text-blue-600 text-lg"></i>
            </div>
            <span className="text-xs text-blue-600 font-medium">التقارير</span>
          </button>
          <button 
            onClick={() => navigate('/financial-alert')}
            className="flex flex-col items-center justify-center py-2"
          >
            <div className="w-6 h-6 flex items-center justify-center mb-1">
              <i className="ri-notification-line text-gray-400 text-lg"></i>
            </div>
            <span className="text-xs text-gray-400">التنبيهات</span>
          </button>
        </div>
      </div>
    </div>
  );
}
